import { useState, type FormEvent } from "react";
import { siteMeta } from "@/mocks/site";
import Reveal from "@/pages/home/components/Reveal";
import SectionHeading from "@/pages/home/components/SectionHeading";

interface FormResponse {
  code?: string;
  message?: string;
  meta?: { message?: string; detail?: string };
}

type Status = "idle" | "submitting" | "success" | "error";

const FORM_ENDPOINT = "https://readdy.ai/api/form/daho7troh653ivfvo2f0";

export default function ContactSection() {
  const [status, setStatus] = useState<Status>("idle");
  const [formError, setFormError] = useState("");

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const guard = String(formData.get("contact_alt") ?? "").trim();
    if (guard.length > 0) {
      setStatus("success");
      form.reset();
      return;
    }
    formData.delete("contact_alt");

    setStatus("submitting");
    setFormError("");

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      payload.append(key, typeof value === "string" ? value : value.name);
    });

    try {
      const response = await fetch(FORM_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });

      const responseText = await response.text();
      let parsed: FormResponse | null = null;
      try {
        parsed = JSON.parse(responseText) as FormResponse;
      } catch {
        parsed = null;
      }

      const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "";
      const isSpam = String(serverMsg).toLowerCase().includes("spam");
      const isSuccess = response.ok && parsed?.code === "OK";

      if (isSuccess && !isSpam) {
        setStatus("success");
        form.reset();
      } else {
        setStatus("error");
        setFormError(isSpam ? "内容未通过校验，请调整描述后重新提交。" : serverMsg || "提交失败，请稍后重试。");
      }
    } catch {
      setStatus("error");
      setFormError("网络异常，提交未成功，请稍后重试。");
    }
  };

  return (
    <section id="contact" className="w-full bg-background-50 px-4 py-16 md:px-8 md:py-24">
      <div className="mx-auto w-full max-w-6xl">
        <SectionHeading
          eyebrow="SUBMIT & CONTACT"
          title="投稿爆料与联系我们"
          desc="你的经历可能正是别人需要的提醒。填写下面表单提交经历，也可直接邮件联系我们。"
        />

        <div className="mt-10 grid grid-cols-1 gap-8 lg:grid-cols-12 lg:gap-10">
          <Reveal className="lg:col-span-5">
            <div className="flex h-full flex-col gap-4">
              <div className="rounded-lg border border-background-200 bg-background-100 p-5 md:p-6">
                <h3 className="flex items-center gap-2 font-heading text-base font-bold text-foreground-950">
                  <i className="ri-mail-send-line text-lg leading-none text-primary-600"></i>
                  直接联系我们
                </h3>
                <ul className="mt-4 flex flex-col gap-4">
                  <li className="flex items-start gap-3">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-primary-100 text-primary-700">
                      <i className="ri-inbox-archive-line text-lg leading-none"></i>
                    </span>
                    <span>
                      <span className="block font-label text-xs text-foreground-500">投稿爆料邮箱</span>
                      <a href={`mailto:${siteMeta.submitEmail}`} rel="nofollow" className="font-heading text-sm font-bold text-foreground-900 hover:text-primary-600">
                        {siteMeta.submitEmail}
                      </a>
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-secondary-100 text-secondary-700">
                      <i className="ri-customer-service-line text-lg leading-none"></i>
                    </span>
                    <span>
                      <span className="block font-label text-xs text-foreground-500">一般咨询邮箱</span>
                      <a href={`mailto:${siteMeta.email}`} rel="nofollow" className="font-heading text-sm font-bold text-foreground-900 hover:text-primary-600">
                        {siteMeta.email}
                      </a>
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-accent-100 text-accent-900">
                      <i className="ri-time-line text-lg leading-none"></i>
                    </span>
                    <span>
                      <span className="block font-label text-xs text-foreground-500">回复时效</span>
                      <span className="font-heading text-sm font-bold text-foreground-900">工作日 24 小时内</span>
                    </span>
                  </li>
                </ul>
              </div>

              <div className="rounded-lg border border-accent-200 bg-accent-50 p-5 md:p-6">
                <h3 className="flex items-center gap-2 font-heading text-sm font-bold text-accent-900">
                  <i className="ri-alert-line text-base leading-none"></i>
                  投稿小提示
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground-700">
                  请尽量写清楚问题出现的场景与时间，并说明是否保留了订单截图、沟通记录等凭证。描述越具体，整理成档案的速度越快。
                </p>
              </div>
            </div>
          </Reveal>

          <Reveal delay={100} className="lg:col-span-7">
            <form
              id="submission-form"
              data-readdy-form="true"
              onSubmit={handleSubmit}
              className="rounded-lg border border-background-200 bg-background-100 p-5 md:p-7"
            >
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                <div className="flex flex-col gap-2">
                  <label htmlFor="brand_name" className="font-label text-sm text-foreground-800">
                    涉及品牌 / 产品
                  </label>
                  <input
                    id="brand_name"
                    name="brand_name"
                    type="text"
                    required
                    placeholder="例如：某品牌扫地机器人"
                    className="rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-1 focus:ring-primary-400"
                  />
                </div>

                <div className="flex flex-col gap-2">
                  <label htmlFor="email" className="font-label text-sm text-foreground-800">
                    联系邮箱
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    required
                    placeholder="便于我们核实与反馈"
                    className="rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-1 focus:ring-primary-400"
                  />
                </div>

                <div className="flex flex-col gap-2 sm:col-span-2">
                  <label htmlFor="category" className="font-label text-sm text-foreground-800">
                    问题类型
                  </label>
                  <select
                    id="category"
                    name="category"
                    defaultValue="quality"
                    className="cursor-pointer rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 focus:border-primary-400 focus:outline-none focus:ring-1 focus:ring-primary-400"
                  >
                    <option value="quality">产品质量问题</option>
                    <option value="service">售后纠纷</option>
                    <option value="marketing">虚假宣传</option>
                    <option value="trap">营销套路</option>
                    <option value="other">其他情况</option>
                  </select>
                </div>

                <div className="flex flex-col gap-2 sm:col-span-2">
                  <label htmlFor="content" className="font-label text-sm text-foreground-800">
                    经历描述 <span className="text-foreground-500">（不超过 500 字）</span>
                  </label>
                  <textarea
                    id="content"
                    name="content"
                    required
                    maxLength={500}
                    rows={6}
                    placeholder="请描述问题出现的时间、具体现象，以及你希望得到的处理结果。"
                    className="resize-none rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm leading-relaxed text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-1 focus:ring-primary-400"
                  ></textarea>
                </div>
              </div>

              <input
                type="text"
                name="contact_alt"
                className="field-aux-caption"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
              />

              <div className="mt-6 flex flex-col items-start gap-4 sm:flex-row sm:items-center sm:justify-between">
                <button
                  type="submit"
                  disabled={status === "submitting"}
                  className="flex w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-7 py-3 font-label text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-70 sm:w-auto"
                >
                  {status === "submitting" ? (
                    <>
                      <i className="ri-loader-4-line animate-spin text-base leading-none"></i>
                      正在提交
                    </>
                  ) : (
                    <>
                      <i className="ri-send-plane-fill text-base leading-none"></i>
                      提交我的经历
                    </>
                  )}
                </button>
                <p className="font-label text-xs leading-relaxed text-foreground-500">
                  提交即表示同意我们在隐去个人信息后整理展示。
                </p>
              </div>

              {status === "success" ? (
                <div className="mt-5 flex items-start gap-3 rounded-md border border-secondary-300 bg-secondary-50 p-4">
                  <i className="ri-checkbox-circle-fill text-lg leading-none text-secondary-600"></i>
                  <p className="text-sm leading-relaxed text-secondary-900">
                    已收到你的投稿，我们会尽快核实。感谢你为其他人提供的参考。
                  </p>
                </div>
              ) : null}

              {status === "error" ? (
                <div className="mt-5 flex items-start gap-3 rounded-md border border-primary-300 bg-primary-50 p-4">
                  <i className="ri-error-warning-fill text-lg leading-none text-primary-600"></i>
                  <p className="text-sm leading-relaxed text-primary-900">{formError || "提交失败，请稍后重试。"}</p>
                </div>
              ) : null}
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
}